
// Note: In a production app, these should be environment variables
// For now, we'll use placeholder values that users can replace
export const cloudinaryConfig = {
  cloudName: 'dlqntmmhh', // Replace with your Cloudinary cloud name
  uploadPreset: 'estancia_corta_upload_preset', // Replace with your unsigned upload preset
};

export const uploadToCloudinary = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', cloudinaryConfig.uploadPreset);

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/image/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    const data = await response.json();
    return data.secure_url;
  } catch (error) {
    console.error('Error uploading to Cloudinary:', error);
    throw error;
  }
};

interface OptimizationOptions {
  width?: number;
  height?: number;
  quality?: 'auto' | number;
  format?: 'auto' | 'webp' | 'avif' | 'jpg' | 'png';
  crop?: 'fill' | 'fit' | 'scale' | 'pad';
  gravity?: 'auto' | 'center' | 'face' | 'faces';
  dpr?: 'auto' | number;
  progressive?: boolean;
  sharpen?: boolean;
}

export const getOptimizedImageUrl = (
  originalUrl: string, 
  options: OptimizationOptions = {}
): string => {
  if (!originalUrl || !originalUrl.includes('cloudinary.com')) {
    return originalUrl;
  }

  const {
    width,
    height,
    quality = 'auto',
    format = 'auto',
    crop = 'fill',
    gravity = 'auto',
    dpr = 'auto',
    progressive = true,
    sharpen = false
  } = options;

  // Extract the base URL and image path
  const urlParts = originalUrl.split('/upload/');
  if (urlParts.length !== 2) return originalUrl;

  const [baseUrl, imagePath] = urlParts;
  
  // Build transformation parameters
  const transformations = [];
  
  if (width) transformations.push(`w_${width}`);
  if (height) transformations.push(`h_${height}`);
  if (quality) transformations.push(`q_${quality}`);
  if (format) transformations.push(`f_${format}`);
  if (crop) transformations.push(`c_${crop}`);
  if (gravity) transformations.push(`g_${gravity}`);
  if (dpr) transformations.push(`dpr_${dpr}`);
  if (progressive) transformations.push('fl_progressive');
  if (sharpen) transformations.push('e_sharpen');

  const transformationString = transformations.join(',');
  
  return `${baseUrl}/upload/${transformationString}/${imagePath}`;
};

export const getResponsiveImageUrl = (
  originalUrl: string,
  breakpoint: 'mobile' | 'tablet' | 'desktop'
): string => {
  const widthMap = {
    mobile: 400,
    tablet: 768,
    desktop: 1200
  };

  return getOptimizedImageUrl(originalUrl, {
    width: widthMap[breakpoint],
    quality: 'auto',
    format: 'auto',
    dpr: 'auto'
  });
};

export const getHeroImageUrl = (
  originalUrl: string,
  isMobile: boolean = false
): string => {
  return getOptimizedImageUrl(originalUrl, {
    width: isMobile ? 400 : 800,
    height: isMobile ? 300 : 500,
    quality: 'auto',
    format: 'auto',
    crop: 'fill',
    gravity: 'auto',
    dpr: 'auto',
    progressive: true
  });
};

export const getPreloadImageUrl = (originalUrl: string): string => {
  return getOptimizedImageUrl(originalUrl, {
    width: 50,
    quality: 20,
    format: 'auto',
    crop: 'fill'
  });
};

export const getPropertyCardImageUrl = (originalUrl: string): string => {
  return getOptimizedImageUrl(originalUrl, {
    width: 600,
    height: 400,
    quality: 95,
    format: 'auto',
    crop: 'fill',
    gravity: 'auto',
    dpr: 'auto',
    progressive: true,
    sharpen: false
  });
};
